import {useStoreOptional} from '../store/StoreContext';
import type {AppDefinition} from '../types';

/**
 * How many items an app is holding for the visitor right now.
 *
 * Two counts earn a badge, and only two: listings queued for comparison, and
 * unanswered messages on the seller's own listings. Both are things the visitor
 * PUT somewhere and will come back for — a badge on anything else is just noise
 * competing with the dock's own animation.
 */
function useBadge(appId: string): number {
  const store = useStoreOptional();
  if (!store) return 0;
  if (appId === 'compare') return store.compare.length;
  if (appId === 'manage' && store.user) {
    const mine = new Set(
      store.listings.filter((listing) => listing.ownerId === store.user!.id).map((l) => l.id),
    );
    return store.inquiries.filter(
      (inquiry) => inquiry.status === 'moi' && mine.has(inquiry.listingId),
    ).length;
  }
  return 0;
}

/**
 * The icon tile, shared by the dock (desktop) and the home screen (mobile).
 *
 * It always fills its host box, so the caller owns the size: the dock hands it a
 * magnifying wrapper, the home screen hands it a fixed 62px cell.
 *
 * The tile itself is style-agnostic: the colour comes from `app.tint` in
 * `data/apps.tsx`, and the finish comes from `.app-tile` in `index.css`.
 */
export function AppIcon({app}: {app: AppDefinition}) {
  const badge = useBadge(app.id);

  return (
    <span
      className={`app-tile relative flex h-full w-full items-center justify-center rounded-[26%] ${app.tint}`}
    >
      <span className="app-tile__gloss pointer-events-none absolute inset-0 rounded-[26%] bg-gradient-to-b from-white/70 to-transparent opacity-70" />
      <span className="relative flex h-full w-full items-center justify-center">{app.icon}</span>
      {badge > 0 ? (
        <span className="pointer-events-none absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold leading-none text-accent-ink shadow-sm">
          {badge > 9 ? '9+' : badge}
        </span>
      ) : null}
    </span>
  );
}
